import { z } from 'zod';

import {
  type DgContent,
  type DgQuestion,
  type ResearchQuestion,
  dgQuestionTypeSchema,
} from './artifacts.ts';
import { guideQuestionsInOrder } from './guide-coverage.ts';

/**
 * The evidence a Brief is written from: for each purchased research question, the guide questions
 * that served it, their poll counts, every written answer as a quote, and its themes when there are
 * any. Keyed on Sigora's own ids, so nothing Remesh names can reach a prompt or a Brief: participants
 * are study-scoped handles (`P07`), quotes and themes carry handles the assembler mints (`Q12`,
 * `T3`), and an option's wording is read from the purchased guide rather than carried here, so the
 * evidence and the guide cannot disagree about what an option said.
 *
 * Whatever assembles it — the seeded generator here, or a reading of a real study's fieldwork — is
 * held to `validateStudyEvidence` before anything is written from it.
 */
export const STUDY_EVIDENCE_SCHEMA_VERSION = 2;

/** Where a bundle came from: the seeded generator, or a real study's fieldwork. */
export const EVIDENCE_SOURCES = ['fixture', 'remesh'] as const;
export type EvidenceSource = (typeof EVIDENCE_SOURCES)[number];

/**
 * What each kind of claim in a bundle was read from. The answers — who answered, every poll count,
 * every quote and the counted population — come from one read; the completes from another; the
 * themes from a third, when there are any. A fixture bundle reads all three from the generator.
 */
export const EVIDENCE_READS = {
  responses: ['fixture', 'remesh_responses'],
  completes: ['fixture', 'remesh_conversation'],
  labels: ['fixture', 'remesh_labels'],
} as const;
export type EvidenceRead = keyof typeof EVIDENCE_READS;

/**
 * Why a request has no evidence to validate, each settled on the request with Try again and no
 * Brief: no assembler for its source here, Remesh out of reach, an analysis not ready, a refusal,
 * fieldwork that is not this study's guide, or an answer that could not be read. Evidence that
 * comes back and breaks its contract is `evidence_invalid`, which is the validator's, not these.
 * Fieldwork nobody answered is none of these: it is a bundle with nobody's answers, stored and
 * written as `no_answers` cards, or declined below the floor.
 */
export const EVIDENCE_UNAVAILABLE_REASONS = [
  'evidence_unavailable',
  'evidence_outage',
  'analysis_not_ready',
  'evidence_refused',
  'evidence_mismatch',
  'evidence_unreadable',
] as const;
export type EvidenceUnavailableReason = (typeof EVIDENCE_UNAVAILABLE_REASONS)[number];

/**
 * Why a research question has no themes to show, in precedence order: no placed open question to
 * code, labels that were not read, a question still being coded, or one that could not be coded.
 * None of them is zero themes.
 */
export const THEMES_UNAVAILABLE_REASONS = [
  'no_open_questions',
  'not_read',
  'coding_pending',
  'coding_failed',
] as const;
export type ThemesUnavailableReason = (typeof THEMES_UNAVAILABLE_REASONS)[number];

/** A participant as the evidence names them: `P` and at least two digits, never a provider id. */
export const PARTICIPANT_HANDLE = /^P\d{2,}$/;

/** A quote's handle, unique across the study. */
export const QUOTE_HANDLE = /^Q[1-9]\d*$/;

/** A theme's handle, unique across the study; never the label, so two equal labels stay two. */
export const THEME_HANDLE = /^T[1-9]\d*$/;

/** The longest written answer the evidence carries. A storage bound, far above a real answer. */
export const MAX_EVIDENCE_TEXT_CHARS = 20_000;

/** The longest theme label the evidence carries. */
export const MAX_THEME_LABEL_CHARS = 200;

const count = z.int().nonnegative();
const evidenceText = z.string().min(1).max(MAX_EVIDENCE_TEXT_CHARS);
const handle = z.string().regex(PARTICIPANT_HANDLE);

const readOf = <From extends readonly [string, ...string[]]>(from: From) =>
  z.strictObject({ from: z.enum(from), read_at: z.iso.datetime() });

const servedQuestionSchema = z.strictObject({
  dg_question_id: z.uuid(),
  type: dgQuestionTypeSchema,
  /** Whether the question reached the conversation. */
  placed: z.boolean(),
  /** How many counted people answered it; `null` exactly when it was not placed. */
  answered: count.nullable(),
});

const pollEvidenceSchema = z.strictObject({
  dg_question_id: z.uuid(),
  multi_select: z.boolean(),
  /** The counted people who answered the poll: every count is out of this. */
  respondents: count,
  /** Every option of the purchased question, in its order, zeros included, with who chose it. */
  options: z.array(z.strictObject({ option_id: z.uuid(), count, members: z.array(handle) })),
});

const quoteEvidenceSchema = z.strictObject({
  quote_id: z.string().regex(QUOTE_HANDLE),
  dg_question_id: z.uuid(),
  participant: handle,
  /** The answer as the participant wrote it: what the Brief shows. */
  text: evidenceText,
  /**
   * The answer as its source holds it, for audit: Remesh's response text byte for byte, equal to
   * `text`; a spreadsheet cell may differ from `text` only by its formula guard.
   */
  raw_cell: evidenceText,
});

const themesUnavailableSchema = z.strictObject({
  status: z.literal('unavailable'),
  reason: z.enum(THEMES_UNAVAILABLE_REASONS),
});

const themesAvailableSchema = z.strictObject({
  status: z.literal('available'),
  /** Each theme counts a person once, however many of their answers it covers. */
  counting: z.literal('once_per_person'),
  /** The people quoted on the research question's placed open questions: every theme is of them. */
  coded_participants: count,
  themes: z.array(
    z.strictObject({
      theme_id: z.string().regex(THEME_HANDLE),
      label: z.string().min(1).max(MAX_THEME_LABEL_CHARS),
      participants: count,
      members: z.array(handle),
      dg_question_ids: z.array(z.uuid()),
      quote_ids: z.array(z.string().regex(QUOTE_HANDLE)),
    }),
  ),
});

const rqEvidenceSchema = z.strictObject({
  rq_id: z.uuid(),
  /**
   * The guide questions that serve this research question, in guide order. Empty when the guide
   * asked nothing about it: that is "not asked", never a reason to read the rest of the study.
   */
  served: z.array(servedQuestionSchema),
  polls: z.array(pollEvidenceSchema),
  quotes: z.array(quoteEvidenceSchema),
  themes: z.discriminatedUnion('status', [themesUnavailableSchema, themesAvailableSchema]),
});

export const studyEvidenceSchema = z.strictObject({
  schema_version: z.literal(STUDY_EVIDENCE_SCHEMA_VERSION),
  source: z.enum(EVIDENCE_SOURCES),
  assembled_at: z.iso.datetime(),
  /** What each kind of claim was read from, and when; `labels` is `null` when none were read. */
  reads: z.strictObject({
    responses: readOf(EVIDENCE_READS.responses),
    completes: readOf(EVIDENCE_READS.completes),
    labels: readOf(EVIDENCE_READS.labels).nullable(),
  }),
  /** The people who completed the study: the Confidence label, the floor and the Method only. */
  completes: z.strictObject({ count }),
  /**
   * The people counted: accepted and not muted. Every per-person number in the bundle is out of
   * this, and a partial participant is counted on what they answered, so it bears no relation to
   * the completes.
   */
  population: z.strictObject({ count }),
  fieldwork: z.strictObject({ ended_at: z.iso.datetime().nullable() }),
  /** Exactly the purchased research questions, in their order. */
  rqs: z.array(rqEvidenceSchema),
});
export type StudyEvidence = z.infer<typeof studyEvidenceSchema>;
export type RqEvidence = StudyEvidence['rqs'][number];
export type ServedQuestionEvidence = RqEvidence['served'][number];
export type PollEvidence = RqEvidence['polls'][number];
export type QuoteEvidence = RqEvidence['quotes'][number];
export type ThemesEvidence = RqEvidence['themes'];

/** What a research question has to show: answers, a guide that never asked it, or no answers. */
export type RqEvidenceState = 'answered' | 'not_asked' | 'no_answers';

/**
 * Whether a research question was asked and answered. Decided from the evidence alone, so a card
 * that has nothing behind it is not settled whatever a synthesis says about it. A research question
 * none of whose questions reached the conversation was not asked, whatever the guide says.
 */
export const rqEvidenceState = (rq: RqEvidence): RqEvidenceState => {
  if (!rq.served.some((question) => question.placed)) {
    return 'not_asked';
  }

  const answered = rq.quotes.length > 0 || rq.polls.some((poll) => poll.respondents > 0);

  return answered ? 'answered' : 'no_answers';
};

/** What a research question's themes are: available, or why not. */
export const themesState = (rq: RqEvidence): 'available' | ThemesUnavailableReason =>
  rq.themes.status === 'available' ? 'available' : rq.themes.reason;

/** The number a participant handle carries: `P07` is 7. */
const handleNumber = (participant: string): number => Number(participant.slice(1));

/**
 * The characters a spreadsheet export guards with a leading apostrophe. A quote's `text` is its raw
 * cell with that one apostrophe taken off, and otherwise the cell itself.
 */
const FORMULA_TRIGGERS = new Set(['=', '+', '-', '@', '\t', '\r']);

const isDisplayReadingOf = (text: string, rawCell: string): boolean =>
  text === rawCell || (rawCell === `'${text}` && FORMULA_TRIGGERS.has(text.charAt(0)));

/** Each rule `validateStudyEvidence` holds a bundle to, named in what it reports. */
export const EVIDENCE_RULES = [
  'shape',
  'rqs',
  'served',
  'polls',
  'quotes',
  'themes',
  'counts',
  'study',
] as const;
export type EvidenceRule = (typeof EVIDENCE_RULES)[number];

export interface EvidenceProblem {
  rule: EvidenceRule;
  message: string;
}

export type EvidenceValidation =
  { valid: true; evidence: StudyEvidence } | { valid: false; problems: EvidenceProblem[] };

/**
 * An option's wording, as the guide has it: the one place a Brief's option text is read from, so the
 * evidence, the handles and the Brief cannot word an option differently. Empty for an option the
 * question does not have, which validated evidence never names.
 */
export const dgOptionText = (question: DgQuestion | undefined, optionId: string): string =>
  question?.options?.find((option) => option.id === optionId)?.text ?? '';

const isPoll = (question: DgQuestion): boolean => question.type !== 'open_response';

/**
 * Holds a bundle to the purchased research questions and guide it claims to be evidence for, and
 * reports every rule it breaks:
 *
 * - `shape` — it parses as `StudyEvidence`.
 * - `rqs` — its research questions are exactly the purchased ones, in their order.
 * - `served` — each one's served questions are exactly the guide questions that say they serve it,
 *   in guide order and of the guide's type; a question has an answer count exactly when it was
 *   placed; and a question serving several research questions is placed and answered the same
 *   under each.
 * - `polls` — every poll is of a served, placed choice question of that research question, and
 *   every placed choice question has exactly one, with exactly that question's options in its
 *   order, `multi_select` as its type says, as many respondents as answered it, and each option
 *   naming who chose it, each person once, as many as it counts. A single-select poll's choosers
 *   are different people on every option and sum to its respondents; a several-answer poll's
 *   choosers are, together, exactly its respondents. A poll serving two research questions counts
 *   the same people the same way under both.
 * - `quotes` — every quote is of a served, placed open question of that research question; quote
 *   ids are unique across the study; one quote per person per question per research question, the
 *   same words for that person and question under every research question it serves, exactly one
 *   quote per person who answered a placed open question — the same people under every research
 *   question it serves; and its text is its raw cell, or the cell without the export's formula
 *   guard.
 * - `themes` — theme ids are unique across the study; available themes are coded from exactly the
 *   people quoted on the research question's placed open questions; each theme names who it covers,
 *   each person once and at least one, as many as it counts and none who was not coded — every one
 *   quoted on a question the theme names — and names only open questions serving its research
 *   question and quotes by its members on those questions, nothing twice.
 * - `counts` — no count is above its respondents; no answer count, respondents, coded people or
 *   people quoted above the counted population; and every participant named is one of it
 *   (`P01` up to the population), written one way: `P` and the place, zero-padded to the
 *   population's digits and at least two. A participant written any other way is reported alone,
 *   before every other rule.
 * - `study` — a fixture bundle, and only one, reads everything it read from the fixture; nothing
 *   was read, and fieldwork did not end, after the bundle was assembled; and each research
 *   question's themes are in the state its guide and the labels read put it in: no placed open
 *   question, then labels not read, then a coding state — so labels were read exactly when some
 *   research question has themes or a coding state.
 *
 * The completes are not held to any count: a partial participant can make a poll's respondents
 * exceed them, and a muted complete can make them exceed the population.
 *
 * A screener or segment column cannot get in: it is not a guide question, so it serves nothing.
 * Evidence that fails is an infrastructure failure, never "insufficient evidence".
 */
export const validateStudyEvidence = (
  input: unknown,
  accepted: { rqs: readonly ResearchQuestion[]; dg: DgContent },
): EvidenceValidation => {
  const parsed = studyEvidenceSchema.safeParse(input);
  if (!parsed.success) {
    return { valid: false, problems: [{ rule: 'shape', message: z.prettifyError(parsed.error) }] };
  }

  const evidence = parsed.data;
  const problems: EvidenceProblem[] = [];
  const report = (rule: EvidenceRule, message: string) => {
    problems.push({ rule, message });
  };

  const purchased = accepted.rqs.map((rq) => rq.id);
  const given = evidence.rqs.map((rq) => rq.rq_id);
  if (purchased.length !== given.length || purchased.some((id, index) => given[index] !== id)) {
    report('rqs', `the research questions are ${given.join(', ')}, not ${purchased.join(', ')}`);
  }

  const guide = guideQuestionsInOrder(accepted.dg);
  const population = evidence.population.count;
  const counted = (participant: string): boolean => {
    const place = handleNumber(participant);
    return place >= 1 && place <= population;
  };
  // Every other rule compares people by handle, so a person has exactly one: two spellings of one
  // place would count as two people.
  const width = String(population).length;
  const aliases = new Set(
    evidence.rqs
      .flatMap((rq) => [
        ...rq.polls.flatMap((poll) => poll.options.flatMap((option) => option.members)),
        ...rq.quotes.map((quote) => quote.participant),
        ...(rq.themes.status === 'available'
          ? rq.themes.themes.flatMap((theme) => theme.members)
          : []),
      ])
      .filter((participant) => participant !== participantHandle(handleNumber(participant), width)),
  );
  if (aliases.size > 0) {
    return {
      valid: false,
      problems: [
        {
          rule: 'counts',
          message: `${[...aliases].join(', ')} ${aliases.size === 1 ? 'is' : 'are'} not written as \`P\` and the place, ${String(Math.max(2, width))} digits wide`,
        },
      ],
    };
  }
  const quoteIds = new Set<string>();
  const themeIds = new Set<string>();
  const quotedPeople = new Set<string>();
  const pollReadings = new Map<string, string>();
  const cellReadings = new Map<string, string>();
  const servedReadings = new Map<string, string>();
  const quotedSets = new Map<string, string>();
  const labelsRead = evidence.reads.labels !== null;
  let coding = false;

  for (const rq of evidence.rqs) {
    const at = `research question ${rq.rq_id}`;
    const serving = guide.filter((question) => question.serves_rq_ids.includes(rq.rq_id));
    const served = new Map(rq.served.map((question) => [question.dg_question_id, question]));
    const servingById = new Map(serving.map((question) => [question.id, question]));

    if (
      serving.length !== rq.served.length ||
      serving.some((question, index) => rq.served[index]?.dg_question_id !== question.id)
    ) {
      report('served', `${at} is served by the guide's questions in guide order, and only those`);
    }

    for (const question of rq.served) {
      const inGuide = servingById.get(question.dg_question_id);
      if (inGuide !== undefined && inGuide.type !== question.type) {
        report('served', `${at}: question ${question.dg_question_id} is a ${inGuide.type}`);
      }
      const reading = JSON.stringify(question);
      if ((servedReadings.get(question.dg_question_id) ?? reading) !== reading) {
        report(
          'served',
          `question ${question.dg_question_id} is placed or answered differently under two research questions`,
        );
      }
      servedReadings.set(question.dg_question_id, reading);
      if (question.placed !== (question.answered !== null)) {
        report(
          'served',
          `${at}: question ${question.dg_question_id} has an answer count exactly when it was placed`,
        );
      }
      if (question.answered !== null && question.answered > population) {
        report(
          'counts',
          `${at}: question ${question.dg_question_id} has more answers than people counted`,
        );
      }
    }

    const polled = new Set<string>();
    for (const poll of rq.polls) {
      const question = servingById.get(poll.dg_question_id);
      if (question === undefined || !isPoll(question) || !served.has(poll.dg_question_id)) {
        report('polls', `${at}: poll ${poll.dg_question_id} is not a choice question serving it`);
        continue;
      }
      if (polled.has(poll.dg_question_id)) {
        report('polls', `${at}: poll ${poll.dg_question_id} is given twice`);
      }
      polled.add(poll.dg_question_id);

      const options = (question.options ?? []).map((option) => option.id);
      const countedOptions = poll.options.map((option) => option.option_id);
      if (
        options.length !== countedOptions.length ||
        options.some((id, index) => countedOptions[index] !== id)
      ) {
        report('polls', `${at}: poll ${poll.dg_question_id} counts exactly its options, in order`);
      }
      if (poll.multi_select !== (question.type === 'multiple_choice')) {
        report('polls', `${at}: poll ${poll.dg_question_id} is ${question.type}`);
      }
      const servedQuestion = served.get(poll.dg_question_id);
      if (servedQuestion?.placed === false) {
        report('polls', `${at}: poll ${poll.dg_question_id} was not placed, yet is counted`);
      }
      const answered = servedQuestion?.answered;
      if (answered !== undefined && answered !== null && answered !== poll.respondents) {
        report(
          'polls',
          `${at}: poll ${poll.dg_question_id} has as many respondents as answered it`,
        );
      }
      if (poll.respondents > population) {
        report(
          'counts',
          `${at}: poll ${poll.dg_question_id} has more respondents than people counted`,
        );
      }
      if (poll.options.some((option) => option.count > poll.respondents)) {
        report('counts', `${at}: poll ${poll.dg_question_id} counts more people than answered it`);
      }
      if (
        poll.options.some(
          (option) =>
            new Set(option.members).size !== option.members.length ||
            option.count !== option.members.length,
        )
      ) {
        report(
          'polls',
          `${at}: poll ${poll.dg_question_id} names each chooser once, as many as it counts`,
        );
      }
      if (poll.options.some((option) => !option.members.every(counted))) {
        report('counts', `${at}: poll ${poll.dg_question_id} names somebody who was not counted`);
      }
      const reading = JSON.stringify([poll.respondents, poll.options]);
      if ((pollReadings.get(poll.dg_question_id) ?? reading) !== reading) {
        report(
          'polls',
          `poll ${poll.dg_question_id} counts differently under two research questions`,
        );
      }
      pollReadings.set(poll.dg_question_id, reading);
      const choosers = poll.options.flatMap((option) => option.members);
      const people = new Set(choosers).size;
      if (!poll.multi_select) {
        const total = poll.options.reduce((sum, option) => sum + option.count, 0);
        if (total !== poll.respondents) {
          report(
            'polls',
            `${at}: single-select poll ${poll.dg_question_id} sums to its respondents`,
          );
        }
        if (people !== choosers.length) {
          report(
            'polls',
            `${at}: single-select poll ${poll.dg_question_id} has somebody choosing two options`,
          );
        }
      }
      if (poll.multi_select && people !== poll.respondents) {
        report(
          'polls',
          `${at}: several-answer poll ${poll.dg_question_id} is chosen by exactly its respondents`,
        );
      }
    }

    const quoted = new Set<string>();
    const quotedQuestions = new Set<string>();
    for (const quote of rq.quotes) {
      quotedQuestions.add(quote.dg_question_id);
      const question = servingById.get(quote.dg_question_id);
      if (question === undefined || isPoll(question) || !served.has(quote.dg_question_id)) {
        report('quotes', `${at}: quote ${quote.quote_id} is not of an open question serving it`);
      }
      if (quoteIds.has(quote.quote_id)) {
        report('quotes', `quote id ${quote.quote_id} is used twice`);
      }
      quoteIds.add(quote.quote_id);

      const key = `${quote.participant} ${quote.dg_question_id}`;
      if (quoted.has(key)) {
        report('quotes', `${at}: ${quote.participant} is quoted twice on one question`);
      }
      quoted.add(key);
      quotedPeople.add(quote.participant);
      if (!counted(quote.participant)) {
        report('counts', `${at}: quote ${quote.quote_id} is from somebody who was not counted`);
      }

      const cell = JSON.stringify([quote.text, quote.raw_cell]);
      if ((cellReadings.get(key) ?? cell) !== cell) {
        report(
          'quotes',
          `${quote.participant}'s answer to ${quote.dg_question_id} reads differently under two research questions`,
        );
      }
      cellReadings.set(key, cell);

      if (!isDisplayReadingOf(quote.text, quote.raw_cell)) {
        report('quotes', `${at}: quote ${quote.quote_id} is not its cell's own words`);
      }
    }
    for (const questionId of quotedQuestions) {
      if (served.get(questionId)?.placed === false) {
        report('quotes', `${at}: question ${questionId} was not placed, yet is quoted`);
      }
    }

    const quotedPeopleOn = new Map<string, string[]>();
    for (const quote of rq.quotes) {
      const people = quotedPeopleOn.get(quote.dg_question_id) ?? [];
      people.push(quote.participant);
      quotedPeopleOn.set(quote.dg_question_id, people);
    }

    // Every placed question has its evidence, by the guide's own type: a choice question exactly
    // one poll, zeros and all, and an open question one quote per person who answered it, the
    // same people under every research question it serves.
    for (const question of rq.served) {
      if (!question.placed || question.answered === null) {
        continue;
      }
      const id = question.dg_question_id;
      const inGuide = servingById.get(id);
      if (inGuide === undefined) {
        continue;
      }
      if (isPoll(inGuide)) {
        if (!polled.has(id)) {
          report('polls', `${at}: choice question ${id} has no poll`);
        }
        continue;
      }
      const people = (quotedPeopleOn.get(id) ?? []).toSorted();
      if (people.length !== question.answered) {
        report('quotes', `${at}: question ${id} has one quote per person who answered it`);
      }
      const set = JSON.stringify(people);
      if ((quotedSets.get(id) ?? set) !== set) {
        report('quotes', `question ${id} quotes different people under two research questions`);
      }
      quotedSets.set(id, set);
    }

    // The themes' state follows from the guide and the labels read, in this order: nothing to
    // code, labels not read, then what coding says.
    const placedOpen = new Set(
      serving
        .filter((question) => !isPoll(question) && served.get(question.id)?.placed === true)
        .map((question) => question.id),
    );
    const state = themesState(rq);
    if (placedOpen.size === 0) {
      if (state !== 'no_open_questions') {
        report('study', `${at} has no placed open question, so its themes are no_open_questions`);
      }
    } else if (!labelsRead) {
      if (state !== 'not_read') {
        report('study', `${at}: no labels were read, so its themes are not_read`);
      }
    } else if (state === 'no_open_questions' || state === 'not_read') {
      report('study', `${at}: its labels were read, so its themes are coded, pending or failed`);
    }
    if (state === 'available' || state === 'coding_pending' || state === 'coding_failed') {
      coding = true;
    }

    if (rq.themes.status === 'available') {
      const quotes = new Map(rq.quotes.map((quote) => [quote.quote_id, quote]));
      // Who is quoted on each question, read once for every theme.
      const quotedOn = new Map<string, Set<string>>();
      for (const quote of rq.quotes) {
        const people = quotedOn.get(quote.dg_question_id) ?? new Set<string>();
        people.add(quote.participant);
        quotedOn.set(quote.dg_question_id, people);
      }
      const coded = new Set([...placedOpen].flatMap((id) => [...(quotedOn.get(id) ?? [])]));
      const { coded_participants: codedCount } = rq.themes;
      if (codedCount !== coded.size) {
        report(
          'themes',
          `${at}: themes are coded from the ${String(coded.size)} people quoted on its open questions`,
        );
      }
      if (codedCount > population) {
        report('counts', `${at}: more people coded than counted`);
      }
      for (const theme of rq.themes.themes) {
        const named = `${at}: theme ${theme.theme_id}`;
        if (themeIds.has(theme.theme_id)) {
          report('themes', `theme id ${theme.theme_id} is used twice`);
        }
        themeIds.add(theme.theme_id);
        if (theme.participants > codedCount) {
          report('themes', `${named} counts more people than were coded`);
        }
        if (
          theme.members.length === 0 ||
          new Set(theme.members).size !== theme.members.length ||
          theme.participants !== theme.members.length
        ) {
          report('themes', `${named} names each of its people once, at least one, as it counts`);
        }
        if (!theme.members.every(counted)) {
          report('counts', `${named} names somebody who was not counted`);
        }
        if (theme.dg_question_ids.some((id) => !placedOpen.has(id))) {
          report('themes', `${named} names a question that is not a placed open one serving it`);
        }
        const members = new Set(theme.members);
        const questions = new Set(theme.dg_question_ids);
        if (
          theme.members.some(
            (member) => !theme.dg_question_ids.some((id) => quotedOn.get(id)?.has(member)),
          )
        ) {
          report('themes', `${named} names somebody quoted on none of its questions`);
        }
        if (
          theme.quote_ids.some((id) => {
            const quote = quotes.get(id);
            return (
              quote === undefined ||
              !questions.has(quote.dg_question_id) ||
              !members.has(quote.participant)
            );
          })
        ) {
          report('themes', `${named} names a quote that is not its members' on its questions`);
        }
        if (
          new Set(theme.dg_question_ids).size !== theme.dg_question_ids.length ||
          new Set(theme.quote_ids).size !== theme.quote_ids.length
        ) {
          report('themes', `${named} names a question or quote twice`);
        }
      }
    }
  }

  if (quotedPeople.size > population) {
    report('counts', 'more people are quoted than were counted');
  }

  const reads = [evidence.reads.responses, evidence.reads.completes, evidence.reads.labels].filter(
    (read) => read !== null,
  );
  const fromFixture = reads.map((read) => read.from === 'fixture');
  if (
    evidence.source === 'fixture'
      ? fromFixture.some((fixture) => !fixture)
      : fromFixture.some((fixture) => fixture)
  ) {
    report('study', 'a fixture bundle, and only one, reads everything from the fixture');
  }
  const assembled = Date.parse(evidence.assembled_at);
  if (reads.some((read) => Date.parse(read.read_at) > assembled)) {
    report('study', 'something was read after the evidence was assembled');
  }
  const ended = evidence.fieldwork.ended_at;
  if (ended !== null && Date.parse(ended) > assembled) {
    report('study', 'fieldwork ended after the evidence was assembled');
  }
  if (labelsRead && !coding) {
    report('study', 'labels were read, yet no research question has themes or a coding state');
  }

  return problems.length === 0 ? { valid: true, evidence } : { valid: false, problems };
};

/** What the seeded generator's bundles look like. */
export const EVIDENCE_SCENARIOS = [
  'known',
  'conflicting',
  'empty_rq',
  'numerals',
  'themes',
] as const;
export type EvidenceScenario = (typeof EVIDENCE_SCENARIOS)[number];

/**
 * A seeded generator of numbers in [0, 1): the same seed gives the same sequence on every run and
 * every machine, which is what makes a fixture bundle reproducible from its seed.
 */
export const seededRandom = (seed: number): (() => number) => {
  let state = seed | 0;

  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
};

/** A participant's handle: `P` and their place, at least two digits wide. */
export const participantHandle = (place: number, width: number): string =>
  `P${String(place).padStart(Math.max(2, width), '0')}`;

const KNOWN_ANSWERS = [
  'The setup took longer than anyone on the team expected.',
  'Nobody owned the rollout, so it drifted after the first week.',
  'It did what we needed once the integrations were connected.',
  'I could not tell which plan we were supposed to be on.',
  'Support answered quickly, which kept us going.',
  'We liked the reports but never got the team into the habit.',
];

const CONFLICTING_ANSWERS = [
  'It was worth every penny for a team like ours.',
  'Honestly it felt overpriced for what it does.',
];

/**
 * Answers that hold numbers in every form a model might be tempted to count from, each written as a
 * person would: digits, a percentage, spelled numbers, the "second time" ordinal beside "half", and
 * a dash-led answer the export guards with an apostrophe.
 */
const NUMERAL_ANSWERS: readonly { text: string; raw_cell: string }[] = [
  { text: 'Half the team never logged in a second time.', raw_cell: '' },
  { text: 'We tried it 3 times before giving up.', raw_cell: '' },
  { text: 'About 40% of my team never opened it.', raw_cell: '' },
  { text: 'It took two weeks and twenty-three emails to get access.', raw_cell: '' },
  { text: '- I stopped using it after 2 days, at $4.99 a seat.', raw_cell: '' },
  { text: 'One in five of us used it daily; the rest, never.', raw_cell: '' },
].map((answer) => ({
  text: answer.text,
  raw_cell: FORMULA_TRIGGERS.has(answer.text.charAt(0)) ? `'${answer.text}` : answer.text,
}));

const THEME_LABELS = ['Setup effort', 'Price fairness', 'Team ownership'];

/** The answers one set of open questions is given: `known` in every scenario but `conflicting`. */
export interface EvidenceAnswers {
  readonly known: readonly string[];
  readonly conflicting: readonly string[];
}

/**
 * The words a generated bundle's participants answer open questions with, in place of the
 * built-in trial-onboarding answers, so a case written for one Study Type reads like that type's
 * fieldwork. `known` answers every scenario but `conflicting`, which answers from `conflicting`;
 * the `numerals` scenario keeps its own answers. `byQuestion` gives an open question of the guide,
 * by its id, answers of its own in place of those, so the answers to a question about one variant
 * are about that variant; every other open question answers from `known` and `conflicting`, and the
 * bundle is otherwise the one the bank without it gives. `themeLabels`, at most three, name the
 * themes the `themes` scenario reads, and a bank used for it must name them. Poll counts follow the
 * guide's options whatever the bank says.
 */
export interface EvidenceAnswerBank extends EvidenceAnswers {
  readonly byQuestion?: Readonly<Record<string, EvidenceAnswers>>;
  readonly themeLabels?: readonly string[];
}

/** The answers every generated bundle gives without a bank of its own. */
const BUILT_IN_ANSWERS: EvidenceAnswerBank = {
  known: KNOWN_ANSWERS,
  conflicting: CONFLICTING_ANSWERS,
  themeLabels: THEME_LABELS,
};

/**
 * Refuses a bank the generator could not answer from: an empty list, its own or a question's, a
 * question's answers keyed to anything but an open question of the guide, more themes than it
 * reads, or a `themes` scenario with no theme labels, which would name a case's themes with words
 * its participants never used.
 */
const assertAnswerBank = (
  bank: EvidenceAnswerBank,
  scenario: EvidenceScenario,
  guide: readonly DgQuestion[],
): void => {
  if (
    [bank, ...Object.values(bank.byQuestion ?? {})].some(
      (answers) => answers.known.length === 0 || answers.conflicting.length === 0,
    )
  ) {
    throw new Error('an answer bank needs at least one known and one conflicting answer');
  }
  for (const id of Object.keys(bank.byQuestion ?? {})) {
    if (!guide.some((question) => question.id === id && question.type === 'open_response')) {
      throw new Error(
        `an answer bank gives answers to ${id}, which is no open question of the guide`,
      );
    }
  }
  if ((bank.themeLabels?.length ?? 0) > THEME_LABELS.length) {
    throw new Error(`an answer bank names at most ${String(THEME_LABELS.length)} theme labels`);
  }
  if (scenario === 'themes' && (bank.themeLabels?.length ?? 0) === 0) {
    throw new Error('an answer bank answering the themes scenario names its theme labels');
  }
};

/** How often a participant skips a question they were shown. */
const SKIP_RATE = 0.08;

interface QuestionAnswers {
  /** Each answering participant's place, in participant order. */
  answering: number[];
  /** A choice question's choosers per option, in option order, each in participant order. */
  members: number[][];
  /** An open question's answer per answering participant, in the same order. */
  cells: { text: string; raw_cell: string }[];
}

/** Option weights: one clear leader, or two near-even leaders for conflicting evidence. */
const optionWeights = (options: number, leader: number, conflicting: boolean): number[] =>
  Array.from({ length: options }, (_, index) => {
    if (index === leader) {
      return 5;
    }
    if (conflicting && index === (leader + 1) % options) {
      return 5;
    }
    return 1;
  });

const pickWeighted = (weights: readonly number[], random: () => number): number => {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let at = random() * total;
  for (const [index, weight] of weights.entries()) {
    at -= weight;
    if (at < 0) {
      return index;
    }
  }
  return weights.length - 1;
};

const answerQuestion = (
  question: DgQuestion,
  {
    completes,
    scenario,
    silent,
    random,
    bank,
  }: {
    completes: number;
    scenario: EvidenceScenario;
    silent: boolean;
    random: () => number;
    bank: EvidenceAnswerBank;
  },
): QuestionAnswers => {
  const answering = silent
    ? []
    : Array.from({ length: completes }, (_, index) => index + 1).filter(
        () => random() >= SKIP_RATE,
      );
  const options = question.options?.length ?? 0;

  if (question.type === 'open_response') {
    const cells = answering.map((_, index) => {
      if (scenario === 'numerals') {
        return NUMERAL_ANSWERS[index % NUMERAL_ANSWERS.length] as {
          text: string;
          raw_cell: string;
        };
      }
      const own = bank.byQuestion?.[question.id] ?? bank;
      const answers = scenario === 'conflicting' ? own.conflicting : own.known;
      const text = answers[Math.floor(random() * answers.length)] as string;
      return { text, raw_cell: text };
    });
    return { answering, members: [], cells };
  }

  const members = Array.from({ length: options }, (): number[] => []);
  const leader = Math.floor(random() * options);
  const weights = optionWeights(options, leader, scenario === 'conflicting');
  for (const place of answering) {
    if (question.type === 'single_choice') {
      members[pickWeighted(weights, random)]?.push(place);
      continue;
    }
    const picks = weights.map((weight) => random() < weight / 10);
    if (!picks.some(Boolean)) {
      picks[pickWeighted(weights, random)] = true;
    }
    picks.forEach((picked, index) => {
      if (picked) {
        members[index]?.push(place);
      }
    });
  }

  return { answering, members, cells: [] };
};

/**
 * A bundle of evidence for a study's purchased research questions and guide, the same for the same
 * inputs: every served question placed, every guide option counted with who chose it, and every
 * written answer a quote. The counted population is the completes, and every read is the fixture's,
 * dated `at`. The research questions the guide does not serve come out not asked, from the guide
 * rather than the scenario.
 *
 * - `known` — clear leaders on every poll, and plain answers.
 * - `conflicting` — two near-even leaders on every poll, and answers that disagree.
 * - `empty_rq` — the last research question served only by questions of its own has no answers
 *   at all, and no other research question loses any; with no such research question, none is
 *   left empty.
 * - `numerals` — answers full of numbers, including one the export guards with an apostrophe.
 * - `themes` — labels are read, and every research question with an open question has its themes
 *   available, each person counted once and every member somebody quoted on the theme's questions.
 *
 * Outside `themes` no labels are read, so a research question with an open question has its themes
 * `not_read`; in every scenario one with none has them `no_open_questions`.
 *
 * `answers` replaces the built-in open answers and theme labels with a bank of the case's own
 * (`EvidenceAnswerBank`); without one, the bundle is exactly the one the built-in answers give.
 *
 * `at` is when the bundle was assembled and when its fieldwork ended, so a synthesis retried for the
 * same request reassembles exactly the same bundle.
 */
export const generateStudyEvidence = ({
  rqs,
  dg,
  completes,
  scenario = 'known',
  seed,
  at,
  answers: bank = BUILT_IN_ANSWERS,
}: {
  rqs: readonly ResearchQuestion[];
  dg: DgContent;
  completes: number;
  scenario?: EvidenceScenario;
  seed: number;
  at: string;
  answers?: EvidenceAnswerBank;
}): StudyEvidence => {
  const guide = guideQuestionsInOrder(dg);
  assertAnswerBank(bank, scenario, guide);
  const random = seededRandom(seed);
  const servingOf = (rqId: string) => guide.filter((each) => each.serves_rq_ids.includes(rqId));

  // The research question left with no answers is the last one served only by questions of its
  // own, so silencing it takes no answer from another research question.
  const ownQuestions = (rqId: string) =>
    servingOf(rqId).every((question) => question.serves_rq_ids.length === 1);
  const silentRq =
    scenario === 'empty_rq'
      ? rqs.findLast((rq) => servingOf(rq.id).length > 0 && ownQuestions(rq.id))?.id
      : undefined;
  const silentQuestions = new Set(
    silentRq === undefined ? [] : servingOf(silentRq).map((question) => question.id),
  );

  const answers = new Map<string, QuestionAnswers>();
  for (const question of guide) {
    answers.set(
      question.id,
      answerQuestion(question, {
        completes,
        scenario,
        silent: silentQuestions.has(question.id),
        random,
        bank,
      }),
    );
  }

  const width = String(completes).length;
  const handleOf = (place: number) => participantHandle(place, width);
  // Labels are read only where there is an open question to code: a study with none reads none.
  const labelsRead =
    scenario === 'themes' &&
    rqs.some((rq) => servingOf(rq.id).some((question) => !isPoll(question)));
  let quoteNumber = 0;
  let themeNumber = 0;

  return {
    schema_version: STUDY_EVIDENCE_SCHEMA_VERSION,
    source: 'fixture',
    assembled_at: at,
    reads: {
      responses: { from: 'fixture', read_at: at },
      completes: { from: 'fixture', read_at: at },
      labels: labelsRead ? { from: 'fixture', read_at: at } : null,
    },
    completes: { count: completes },
    population: { count: completes },
    fieldwork: { ended_at: at },
    rqs: rqs.map((rq): RqEvidence => {
      const serving = servingOf(rq.id);
      const answered = (question: DgQuestion) => answers.get(question.id) as QuestionAnswers;

      const quotes = serving
        .filter((question) => !isPoll(question))
        .flatMap((question) =>
          answered(question).answering.map((place, index): QuoteEvidence => {
            quoteNumber += 1;
            const cell = answered(question).cells[index] as { text: string; raw_cell: string };
            return {
              quote_id: `Q${String(quoteNumber)}`,
              dg_question_id: question.id,
              participant: handleOf(place),
              text: cell.text,
              raw_cell: cell.raw_cell,
            };
          }),
        );

      const openQuestions = serving.filter((question) => !isPoll(question)).map((each) => each.id);
      // Everybody quoted, in participant order: the people the themes are coded from.
      const coded = [...new Set(quotes.map((quote) => quote.participant))].toSorted();
      let themes: ThemesEvidence;
      if (openQuestions.length === 0) {
        themes = { status: 'unavailable', reason: 'no_open_questions' };
      } else if (!labelsRead) {
        themes = { status: 'unavailable', reason: 'not_read' };
      } else {
        themes = {
          status: 'available',
          counting: 'once_per_person',
          coded_participants: coded.length,
          themes: (bank.themeLabels ?? []).flatMap((label, index) => {
            const members = coded.slice(0, Math.floor((coded.length * (3 - index)) / 4));
            if (members.length === 0) {
              return [];
            }
            const chosen = new Set(members);
            themeNumber += 1;
            return [
              {
                theme_id: `T${String(themeNumber)}`,
                label,
                participants: members.length,
                members,
                dg_question_ids: openQuestions,
                quote_ids: quotes
                  .filter((quote) => chosen.has(quote.participant))
                  .slice(index, index + 2)
                  .map((quote) => quote.quote_id),
              },
            ];
          }),
        };
      }

      return {
        rq_id: rq.id,
        served: serving.map((question) => ({
          dg_question_id: question.id,
          type: question.type,
          placed: true,
          answered: answered(question).answering.length,
        })),
        polls: serving.filter(isPoll).map((question) => ({
          dg_question_id: question.id,
          multi_select: question.type === 'multiple_choice',
          respondents: answered(question).answering.length,
          options: (question.options ?? []).map((option, index) => {
            const members = (answered(question).members[index] ?? []).map(handleOf);
            return { option_id: option.id, count: members.length, members };
          }),
        })),
        quotes,
        themes,
      };
    }),
  };
};
